import { getCityListSuggestByName, getCityListSuggestByZip } from "../../../api/admin/funedataReq";
import { useEffect, useRef, useState } from "react";
import useData from "../../../hooks/useData";
import useAuth from "../../../hooks/useAuth";
import { flushSync } from 'react-dom';
import { normalizeZipCode } from "../../../helper/tools";

// Helper pour créer un événement synthétique valide
const createSyntheticEvent = (name, value) => ({
    target: {
        name,
        value,
        validity: { valid: true },
        required: false,
        parentElement: { classList: { add: () => { }, remove: () => { } } }
    }
});

export default function CityNameZipSuggest({ city, zipcode, zipField, cityField, type, onChange, required = true, disabled = false }) {

    const FUNEDATA_CREDS = process.env.REACT_APP_FUNEDATA_CREDS;
    const { auth } = useAuth();
    const token = auth?.token;
    const selectCityRef = useRef()
    const cityInputRef = useRef()
    const zipInputRef = useRef()
    const isInitialMount = useRef(true);
    const ignorePropsUpdate = useRef(false);
    const [cities, setCities] = useState([]);
    const [selectedCityData, setSelectedCityData] = useState(null);
    const [localCity, setLocalCity] = useState(city || "");
    const [localZip, setLocalZip] = useState(zipcode || "");
    const { selectedCity, setSelectedCity } = useData({});
    const { selectedBirthplace, setSelectedBirthplace } = useData({});
    const { selectedDeathplace, setSelectedDeathplace } = useData({});

    useEffect(() => {
        setCities([]);
        setSelectedCityData(null);
        if (selectCityRef.current) {
            selectCityRef.current.style.display = "none";
        }
    }, [type]);

    useEffect(() => {
        if (ignorePropsUpdate.current) {
            return;
        }

        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (city || zipcode) {
                setLocalCity(city || "");
                setLocalZip(zipcode || "");
            }
            return;
        }

        setLocalCity(city || "");
        setLocalZip(zipcode || "");
    }, [city, zipcode]);


    const handleChangeCitySearch = (e) => {
        let keyword = e.target.value;
        setLocalCity(keyword);

        if (onChange) {
            onChange(e);
        }

        if (keyword.length > 1 && typeof (keyword) === "string") {
            apiCityListAllByName(keyword.trim());
            if (selectCityRef.current) {
                selectCityRef.current.style.display = "block";
            }
        } else if (keyword.length <= 1) {
            setCities([]);
            if (!localZip || localZip.length <= 1) {
                if (selectCityRef.current) {
                    selectCityRef.current.style.display = "none";
                }
            }
        }
    }

    const handleZipCodeChange = (e) => {
        let keyword = e.target.value;
        setLocalZip(keyword);

        if (onChange) {
            onChange(e);
        }

        if (keyword.length > 1) {
            apiCityListAllByZip(keyword.trim());
            if (selectCityRef.current) {
                selectCityRef.current.style.display = "block";
            }
        } else if (keyword.length <= 1) {
            setCities([]);
            if (!localCity || localCity.length <= 1) {
                if (selectCityRef.current) {
                    selectCityRef.current.style.display = "none";
                }
            }
        }
    }

    const handleZipBlur = (e) => {
        // Normaliser le code postal au bon format avant de le propager
        const normalizedZip = normalizeZipCode(localZip);

        // Mettre à jour l'état local si le code postal a été normalisé
        if (normalizedZip !== localZip) {
            setLocalZip(normalizedZip);
        }

        if (onChange) {
            onChange(createSyntheticEvent(zipField, normalizedZip));
        }
    }

    const handleCityBlur = (e) => {
        if (onChange) {
            onChange(createSyntheticEvent(cityField, localCity));
        }
    }


    const apiCityListAllByZip = async (keyword) => {
        let body = {
            creds: FUNEDATA_CREDS,
            keyword: keyword
        };
        const response = await getCityListSuggestByZip(body, token);
        if (!Array.isArray(response)) {
            console.error("Erreur lors de la recherche de ville par code postal :", response);
            setCities([]);
            return;
        }
        setCities(response);
    };

    const apiCityListAllByName = async (keyword) => {
        const trimmedKeyword = keyword.trim();

        if (keyword !== trimmedKeyword || keyword.length === 0) {
            return;
        }

        let body = {
            creds: FUNEDATA_CREDS,
            keyword: trimmedKeyword
        };
        const response = await getCityListSuggestByName(body, token);

        if (!Array.isArray(response)) {
            console.error("Erreur lors de la recherche de ville par nom :", response);
            setCities([]);
            return;
        }

        const sortedResponse = [...response].sort((a, b) => {
            const aName = (a?.name || a?.city || a?.label || '').trim();
            const bName = (b?.name || b?.city || b?.label || '').trim();

            const normalizeString = (str) => str
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");

            const normalizedAName = normalizeString(aName);
            const normalizedBName = normalizeString(bName);
            const normalizedKeyword = normalizeString(trimmedKeyword);

            const aStartsWith = normalizedAName.startsWith(normalizedKeyword);
            const bStartsWith = normalizedBName.startsWith(normalizedKeyword);

            if (aStartsWith && !bStartsWith) return -1
            if (!aStartsWith && bStartsWith) return 1;

            if (aStartsWith && bStartsWith) {
                if (aName.length !== bName.length) {
                    return aName.length - bName.length;
                }
            }

            return aName.localeCompare(bName);
        });

        setCities(sortedResponse || []);
    };

    const handleChangeSelected = () => {
        // Ne rien faire si aucune valeur n'est sélectionnée
        if (!selectCityRef.current?.value || selectCityRef.current.value === "") {
            return;
        }

        const selectedData = cities?.filter(city => city?.id == selectCityRef.current.value)[0];

        if (!selectedData) return;

        let cleanCityName = '';
        let cleanZip = '';

        if (selectedData?.city_zip && selectedData.city_zip.includes('(')) {
            const match = selectedData.city_zip.match(/^(.+?)\s*\((\d{5})\)$/);
            if (match) {
                cleanCityName = match[1].trim();
                cleanZip = match[2].trim();
            }
        }

        if (!cleanCityName && !cleanZip && selectedData?.city_zip) {
            const parts = selectedData.city_zip.split(' ');
            if (parts.length >= 2 && /^\d{5}$/.test(parts[0])) {
                cleanZip = parts[0].trim();
                cleanCityName = parts.slice(1).join(' ').trim();
            }
        }

        if (!cleanCityName && selectedData?.name) {
            cleanCityName = selectedData.name.trim();
        } else if (!cleanCityName && selectedData?.city) {
            cleanCityName = selectedData.city.trim();
        }

        if (!cleanZip && selectedData?.zip) {
            cleanZip = selectedData.zip.trim();
        } else if (!cleanZip && selectedData?.zipcode) {
            cleanZip = selectedData.zipcode.trim();
        }

        // Normaliser le code postal au format 5 chiffres
        cleanZip = normalizeZipCode(cleanZip);

        // Mettre à jour selectedData avec les valeurs nettoyées
        const updatedSelectedData = {
            ...selectedData,
            name: cleanCityName,
            zip: cleanZip,
            city: cleanCityName,
            zipcode: cleanZip,
            city_zip: `${cleanCityName} (${cleanZip})` // Pour le useEffect du parent
        };

        // Mise à jour synchrone des valeurs locales
        flushSync(() => {
            setLocalCity(cleanCityName);
            setLocalZip(cleanZip);
            setSelectedCityData(updatedSelectedData);
        });

        // Stocker dans le contexte avec les valeurs nettoyées
        // Le useEffect du parent se déclenchera et mettra à jour ses propres valeurs
        if (type === "birth") {
            setSelectedBirthplace(updatedSelectedData);
        } else if (type === "death") {
            setSelectedDeathplace(updatedSelectedData);
        } else if (type === "actual") {
            setSelectedCity(updatedSelectedData);
        } else {
            // Pour les autres types (tutor, etc.), appeler onChange directement
            if (onChange) {
                onChange(createSyntheticEvent(zipField, cleanZip));
                onChange(createSyntheticEvent(cityField, cleanCityName));
            }
        }

        ignorePropsUpdate.current = true;
        setTimeout(() => {
            ignorePropsUpdate.current = false;
        }, 500);


        // Masquer le dropdown après la sélection
        setCities([]);
        if (selectCityRef.current) {
            selectCityRef.current.style.display = "none";
        }
    }

    return (
        <>
            <div className="form-input">
                <label className={required ? "required" : ""} htmlFor={zipField}>Code postal </label>
                <input
                    ref={zipInputRef}
                    name={zipField}
                    value={localZip}
                    onChange={handleZipCodeChange}
                    onBlur={handleZipBlur}
                    type="text"
                    id={zipField}
                    required={required}
                    disabled={disabled}
                />
            </div>
            {(cities?.length > 0) &&
                <div className="form-input city-select-dropdown">
                    <label>Recherche de Ville</label>
                    <select onChange={handleChangeSelected} ref={selectCityRef} id="city-select" name="city-select" defaultValue="">
                        <option value="" disabled>Sélectionnez une ville</option>
                        {cities?.map((city, index) => (
                            <option name="city-select" key={index} value={city?.id}>{city?.city_zip}</option>
                        ))}
                    </select>
                    <span><b>{selectedCityData?.city_zip || ""}</b></span>
                </div>
            }
            <div className="form-input">
                <label className={required ? "required" : ""} htmlFor={cityField}>Ville </label>
                <input
                    ref={cityInputRef}
                    name={cityField}
                    value={localCity}
                    onChange={handleChangeCitySearch}
                    onBlur={handleCityBlur}
                    type="text"
                    id={cityField}
                    required={required}
                    disabled={disabled}
                />
            </div>
        </>
    )
}
